import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconDelete from '../../assets/icon_delete.svg';
import AppSpinner from '../../components/AppSpinner';

const PHQ9_INTERPRETATIONS: { [key: string]: string } = {
  '0-4': '정상 범위입니다. 우울 증상이 거의 없거나 미미합니다.',
  '5-9': '가벼운 수준의 우울 증상입니다. 상태를 지속적으로 관찰하는 것이 좋습니다.',
  '10-14': '중간 정도 수준의 우울 증상입니다. 전문가와의 상담을 고려해볼 수 있습니다.',
  '15-19': '심한 수준의 우울 증상입니다. 전문가의 도움이 필요할 수 있습니다.',
  '20-27': '매우 심한 수준의 우울 증상입니다. 반드시 전문가의 도움이 필요합니다.',
};

const GAD7_INTERPRETATIONS: { [key: string]: string } = {
  '0-4': '정상 범위입니다. 불안 증상이 거의 없습니다.',
  '5-9': '가벼운 수준의 불안입니다. 일상에 큰 지장은 없지만, 스트레스 관리가 필요할 수 있습니다.',
  '10-14':
    '중간 정도 수준의 불안입니다. 불안으로 인해 일상에 불편함을 느낄 수 있으며, 전문가와의 상담을 고려해볼 수 있습니다.',
  '15-21': '심한 수준의 불안입니다. 전문가의 도움이 필요합니다.',
};

const PSS_INTERPRETATIONS: { [key: string]: string } = {
  '0-13': '정상 범위입니다. 일상적인 스트레스를 잘 관리하고 있습니다.',
  '14-26':
    '중간 수준의 스트레스입니다. 스트레스가 다소 높게 느껴질 수 있으며, 휴식과 이완이 필요합니다.',
  '27-40':
    '높은 수준의 스트레스입니다. 스트레스로 인해 심리적, 신체적 어려움을 겪을 수 있으니 전문가의 도움이 필요할 수 있습니다.',
};

const RSES_INTERPRETATIONS: { [key: string]: string } = {
  '0-15':
    '자존감 수준이 낮습니다. 자신에 대한 부정적인 평가가 많을 수 있으며, 전문가와 함께 자존감을 탐색해보는 것이 좋습니다.',
  '16-25': '평균적인 자존감 수준입니다. 상황에 따라 자신감이 변동될 수 있습니다.',
  '26-30':
    '자존감 수준이 높습니다. 자신을 긍정적으로 평가하고 있으며, 건강한 정신 상태를 유지하고 있습니다.',
};

const PCL5_INTERPRETATIONS: { [key: string]: string } = {
  '0-37': '외상 후 스트레스 증상이 거의 없거나 낮은 수준입니다.',
  '38-80':
    '외상 후 스트레스 증상을 경험하고 있을 가능성이 있습니다. 증상이 일상 생활에 미치는 영향을 고려하여 전문가와의 상담을 고려해볼 수 있습니다.',
};

const BDI_INTERPRETATIONS: { [key: string]: string } = {
  '0-9': '우울하지 않은 상태이거나, 우울 증상이 거의 없는 최소한의 수준입니다.',
  '10-15': '가벼운 우울 상태(경도 우울)를 보이고 있습니다. 기분 변화에 주의를 기울여보세요.',
  '16-23': '중간 수준의 우울 상태(중등도 우울)가 의심됩니다. 전문가 상담을 고려해볼 수 있습니다.',
  '24-63':
    '심각한 수준의 우울 상태가 의심됩니다. 반드시 전문가와 상담하여 도움을 받으시기 바랍니다.',
};

interface TestResultData {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    userId: string;
    email: string;
  } | null;
  testType: string;
  score: number;
  createdAt: string;
}

const AdminTestResultListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [results, setResults] = useState<TestResultData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchType, setSearchType] = useState<string>('all');
  const [keyword, setKeyword] = useState<string>('');
  const [testTypeFilter, setTestTypeFilter] = useState<string>('all');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const itemsPerPage = 20;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const fetchResults = async (page: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/test-results', {
        params: {
          page,
          limit: itemsPerPage,
          keyword,
          testType: testTypeFilter,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setResults(response.data.results);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalResults);
      setCurrentPage(response.data.currentPage);
    } catch (err) {
      setError('심리검사 결과 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchResults(1);
  }, [isAuthenticated]);

  const handleSearch = () => {
    fetchResults(1);
    setSelectedIds([]);
  };

  const handleReset = () => {
    setKeyword('');
    setTestTypeFilter('all');
    // We need to wait for state updates or just call fetch with default values
    // To be safe, let's use a function that takes values
  };

  // Improved reset
  const onReset = () => {
      setKeyword('');
      setTestTypeFilter('all');
      // Directly fetch with reset values
      const token = localStorage.getItem('token');
      axios.get('/api/admin/test-results', {
        params: {
          page: 1,
          limit: itemsPerPage,
          keyword: '',
          testType: 'all',
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then(response => {
        setResults(response.data.results);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalResults);
        setCurrentPage(1);
        setSelectedIds([]);
      });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchResults(page);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = results.map(r => r._id);
      setSelectedIds(prev => Array.from(new Set([...prev, ...allIds])));
    } else {
      const pageIds = results.map(r => r._id);
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedIds.length}개의 결과를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        selectedIds.map(id =>
          axios.delete(`/api/admin/test-results/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      alert('삭제되었습니다.');
      fetchResults(currentPage);
      setSelectedIds([]);
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  const getTestName = (type: string) => {
    const names: { [key: string]: string } = {
      'phq-9': '우울증 검사 (PHQ-9)',
      'gad-7': '불안도 검사 (GAD-7)',
      'pss': '스트레스 검사 (PSS)',
      'rses': '자존감 검사 (RSES)',
      'pcl-5': '외상 후 스트레스 검사 (PCL-5)',
      'bdi': '우울 증상 설문 (BDI)',
    };
    return names[type] || type;
  };

  const getInterpretation = (type: string, score: number) => {
    let interpretations: { [key: string]: string } = {};
    if (type === 'phq-9') interpretations = PHQ9_INTERPRETATIONS;
    else if (type === 'gad-7') interpretations = GAD7_INTERPRETATIONS;
    else if (type === 'pss') interpretations = PSS_INTERPRETATIONS;
    else if (type === 'rses') interpretations = RSES_INTERPRETATIONS;
    else if (type === 'pcl-5') interpretations = PCL5_INTERPRETATIONS;
    else if (type === 'bdi') interpretations = BDI_INTERPRETATIONS;
    else return '';

    for (const range in interpretations) {
      const [min, max] = range.split('-').map(Number);
      if (score >= min && score <= max) {
        return interpretations[range];
      }
    }
    return '';
  };

  if (loading && results.length === 0) return <AppSpinner fullscreen tip="Loading..." />;

  return (
    <div className="admin-page">
      <h1>심리검사 결과 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>검사 종류</label>
          <select value={testTypeFilter} onChange={e => setTestTypeFilter(e.target.value)}>
            <option value="all">전체</option>
            <option value="phq-9">우울증 (PHQ-9)</option>
            <option value="gad-7">불안도 (GAD-7)</option>
            <option value="pss">스트레스 (PSS)</option>
            <option value="rses">자존감 (RSES)</option>
            <option value="pcl-5">PTSD (PCL-5)</option>
            <option value="bdi">우울 (BDI)</option>
          </select>
        </div>

        <div className="filter-group">
          <label>사용자 검색</label>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="이름 또는 아이디를 입력하세요"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
          <button className="btn-reset" onClick={onReset}>
            초기화
          </button>
        </div>
      </div>

      <div className="list-info">
        총 <span className="count">{totalCount}</span>건 | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table test-result-list-table">
          <colgroup>
            <col width="50px" />
            <col width="6%" />
            <col width="15%" />
            <col width="20%" />
            <col width="10%" />
            <col width="35%" />
            <col width="15%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={results.length > 0 && results.every(r => selectedIds.includes(r._id))}
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark"></span>
                </label>
              </th>
              <th>No</th>
              <th>사용자 (아이디)</th>
              <th>검사 종류</th>
              <th>점수</th>
              <th>결과 해석</th>
              <th>검사 일시</th>
            </tr>
          </thead>
          <tbody>
            {results.length > 0 ? (
              results.map((result, index) => (
                <tr key={result._id}>
                  <td>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(result._id)}
                        onChange={() => handleSelectOne(result._id)}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </td>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    {result.user ? (
                      <>
                        {result.user.displayName} ({result.user.userId || '소셜로그인'})
                      </>
                    ) : (
                      <span style={{ color: '#999' }}>(알 수 없음)</span>
                    )}
                  </td>
                  <td>{getTestName(result.testType)}</td>
                  <td style={{ fontWeight: '700' }}>{result.score}점</td>
                  <td style={{ textAlign: 'left', paddingLeft: '10px', fontSize: '0.85rem' }}>
                      {getInterpretation(result.testType, result.score)}
                  </td>
                  <td>
                    {new Date(result.createdAt).toLocaleDateString()}{' '}
                    {new Date(result.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="no-data">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div className="pagination-container">
        <div className="pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <img src={leftArrow1} alt="화살표" />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <img src={leftArrow2} alt="화살표" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p >= currentPage - 2 && p <= currentPage + 2) // Limit displayed pages
            .map(page => (
              <button
                key={page}
                className={currentPage === page ? 'active' : ''}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow2} alt="화살표" />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow1} alt="화살표" />
          </button>
        </div>
        <div className="bottom-actions">
          <button className="btn-delete-selection" onClick={handleDeleteSelected}>
            <img src={iconDelete} alt="삭제" />
            삭제
          </button>        
        </div>
      </div>
    </div>
  );
};

export default AdminTestResultListPage;
